import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useState } from 'react'
import { AvisoDeTrabalho } from '../components/AvisoDeTrabalho'
import { IconMenu } from '../components/icons'
import { NavRail } from '../components/NavRail'
import { authClient, useSession } from '../lib/auth-client'
import { t, tRico, trocarIdioma } from '../lib/i18n'
import { NavMenuProvider } from '../lib/nav'
import { SomProvider, useSomDeAviso, useTituloComContador } from '../lib/notify'
import { useRealtime } from '../lib/realtime'
import { useTheme } from '../lib/theme'
import { setActiveWorkspace, useTRPC } from '../lib/trpc'

export const Route = createFileRoute('/_app')({ component: AppLayout })

const WORKSPACE_KEY = 'reydelgreen:workspace'

function AppLayout() {
  const navigate = useNavigate()
  const session = useSession()
  const trpc = useTRPC()
  const [menuOpen, setMenuOpen] = useState(false)
  const [theme, toggleTheme] = useTheme()
  const [workspaceId, setWorkspaceId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : localStorage.getItem(WORKSPACE_KEY),
  )

  // Precisa valer antes de qualquer query sair — por isso fica no corpo do
  // componente e não num efeito, que rodaria tarde demais.
  setActiveWorkspace(workspaceId)

  useEffect(() => {
    if (!session.isPending && !session.data) navigate({ to: '/login' })
  }, [session.isPending, session.data, navigate])

  const me = useQuery({ ...trpc.me.queryOptions(), enabled: Boolean(session.data) })
  const workspaces = me.data?.workspaces ?? []

  // A escolha gravada no usuário manda, e é o que faz o idioma seguir a pessoa
  // para um aparelho que nunca a viu. `null` é quem nunca escolheu: aí fica
  // valendo o que o navegador pediu, decidido antes do primeiro quadro em
  // `main.tsx`. `trocarIdioma` sai cedo quando já é o idioma corrente, então a
  // remontagem só acontece quando há de fato o que mudar.
  const idiomaDoUsuario = me.data?.idioma ?? null
  useEffect(() => {
    if (idiomaDoUsuario) trocarIdioma(idiomaDoUsuario)
  }, [idiomaDoUsuario])

  // Workspace salvo que não existe mais (saiu da equipe, foi apagado): cai
  // para o primeiro disponível em vez de deixar a tela travada em erro.
  useEffect(() => {
    if (me.isPending || workspaces.length === 0) return
    if (!workspaces.some((workspace) => workspace.id === workspaceId)) {
      const fallback = workspaces[0]!.id
      localStorage.setItem(WORKSPACE_KEY, fallback)
      setWorkspaceId(fallback)
    }
  }, [me.isPending, workspaces, workspaceId])

  const som = useSomDeAviso()
  const connection = useRealtime(workspaceId, som.avisar)

  const unread = useQuery({
    ...trpc.conversation.unreadBadge.queryOptions(),
    enabled: Boolean(workspaceId),
  })
  const upcoming = useQuery({
    ...trpc.conversation.upcoming.queryOptions(),
    enabled: Boolean(workspaceId),
  })

  // Conversas esperando resposta, não mensagens acumuladas: é o que o título
  // da guia e o selo do menu precisam dizer.
  const unreadTotal = unread.data?.conversations ?? 0

  // Antes dos returns antecipados: hook não pode ficar atrás de condicional, e
  // o título precisa valer inclusive nas telas de carregamento.
  useTituloComContador(unreadTotal)

  if (session.isPending || (session.data && me.isPending)) {
    return <FullScreen>{t('Carregando…')}</FullScreen>
  }
  if (!session.data) return <FullScreen>{t('Redirecionando…')}</FullScreen>

  if (workspaces.length === 0) {
    return (
      <CreateWorkspace
        onCreated={(id) => {
          localStorage.setItem(WORKSPACE_KEY, id)
          setWorkspaceId(id)
        }}
      />
    )
  }

  if (!workspaceId) return <FullScreen>{t('Carregando workspace…')}</FullScreen>

  return (
    /**
     * `app-shell` reserva as áreas seguras do aparelho — recorte da câmera em
     * cima, notch nas laterais em paisagem. Fica aqui, na casca, e não em cada
     * tela: qualquer página nova nasce protegida, e o inset nunca é aplicado
     * duas vezes. A base é exceção: reservada aqui ela virava uma faixa parada
     * no rodapé de toda página. Composer e gaveta encostam na borda de
     * propósito (decisão do dono); só o conteúdo que rola reserva a barrinha
     * (`.fim-seguro` e a lista do inbox).
     *
     * Sem `h-full`: a altura mora na própria `.app-shell`, que se mede pela
     * janela real do aparelho e não pelo viewport curto que o PWA do iOS
     * entrega. Um utilitário aqui venceria a regra de lá.
     */
    <div className="app-shell flex bg-paper">
      <NavRail
        userName={session.data.user.name}
        userEmail={session.data.user.email}
        unreadTotal={unreadTotal}
        scheduledTotal={upcoming.data?.length ?? 0}
        mobileOpen={menuOpen}
        theme={theme}
        onToggleTheme={toggleTheme}
        onCloseMobile={() => setMenuOpen(false)}
        onSignOut={() => authClient.signOut().then(() => window.location.assign('/login'))}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Barra do celular: sem ela, toda tela que não fosse o Inbox virava
            beco sem saída — o menu só existia no hambúrguer da lista de
            conversas, e as outras páginas não tinham como abrir o menu. */}
        <header className="barra-mobile flex flex-none items-center gap-1 border-b border-line bg-surface px-2 pb-1 md:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('Abrir menu')}
            className="inline-flex size-9 flex-none items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink"
          >
            <IconMenu className="size-5" />
          </button>
          {/* Só "CRM", na cor da marca — o mesmo nome do ícone instalado. */}
          <span className="text-xs font-bold uppercase tracking-wide text-accent-ink">CRM</span>
        </header>

        {connection !== 'open' && <ConnectionBanner state={connection} />}
        {/* Abaixo do aviso de conexão: sem rede, saber que uma importação
            está rodando é a segunda informação, não a primeira. */}
        <AvisoDeTrabalho />
        <div className="min-h-0 flex-1">
          <NavMenuProvider value={{ open: () => setMenuOpen(true) }}>
            <SomProvider value={som}>
              <Outlet />
            </SomProvider>
          </NavMenuProvider>
        </div>
      </div>
    </div>
  )
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return <div className="grid h-full place-items-center text-sm text-ink-3">{children}</div>
}

/**
 * Um socket caído faz o inbox parar de atualizar sem sinal nenhum — numa
 * ferramenta de atendimento isso é pior do que um erro visível, porque o
 * agente acha que está em dia. Por isso a faixa ocupa a largura toda.
 */
function ConnectionBanner({ state }: { state: 'connecting' | 'closed' }) {
  const connecting = state === 'connecting'
  return (
    <div
      role="status"
      className="flex flex-none items-center justify-center gap-2 px-3 py-1.5 text-2xs font-semibold"
      style={{
        color: connecting ? 'var(--st-atend)' : 'var(--danger)',
        background: connecting ? 'var(--st-atend-soft)' : 'var(--danger-soft)',
      }}
    >
      <span aria-hidden className="size-1.5 rounded-full" style={{ background: 'currentColor' }} />
      {connecting ? t('Reconectando…') : t('Sem conexão — mensagens novas podem não aparecer')}
    </div>
  )
}

function CreateWorkspace({ onCreated }: { onCreated: (id: string) => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')

  const create = useMutation({
    ...trpc.workspace.create.mutationOptions(),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: [['me']] })
      onCreated(result.id)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    // Os dois inboxes de partida nascem com o nome no idioma de quem está
    // criando: viram dado no banco, e um "Suporte" em português na conta de um
    // cliente chileno ficaria ali para sempre até alguém renomear à mão.
    create.mutate({ name, inboxes: [t('Comercial'), t('Suporte')] })
  }

  return (
    <main className="grid h-full place-items-center px-5">
      <form onSubmit={submit} className="w-full max-w-md">
        <h1 className="font-display text-2xl">{t('Criar seu workspace')}</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-2">
          {tRico(
            'Vamos começar com dois inboxes — {comercial} e {suporte} — cada um com seu próprio funil e sua própria equipe. Você pode renomear, remover ou criar outros depois.',
            {
              comercial: <strong>{t('Comercial')}</strong>,
              suporte: <strong>{t('Suporte')}</strong>,
            },
          )}
        </p>

        <label className="mt-6 block">
          <span className="mb-1.5 block text-2xs font-semibold uppercase tracking-wider text-ink-3">
            {t('Nome da empresa')}
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            minLength={2}
            className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
          />
        </label>

        {create.error && (
          <p role="alert" className="mt-3 text-xs text-danger">
            {t(create.error.message)}
          </p>
        )}

        <button
          type="submit"
          disabled={create.isPending}
          className="mt-5 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-on-accent disabled:opacity-50"
        >
          {create.isPending ? t('Criando…') : t('Criar workspace')}
        </button>
      </form>
    </main>
  )
}
